import type { AuthUser } from '@/core/types/user.types';
import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';
import { getUserColor } from '@/core/lib/get-user-color';
import i18n from '@/core/i18n/config';
import { useUIStore, type TimeFormat } from '@/core/stores/ui.store';

interface ApiPreferencesResponse {
  language: string | null;
  timezone: string | null;
  dateFormat: string | null;
  timeFormat: string | null;
}

interface ApiIdentityResponse {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl?: string | null;
  color?: string;
  isActive: boolean;
  lastLoginAt: string;
  createdAt: string;
  preferences?: ApiPreferencesResponse | null;
}

export const authService = {
  /**
   * Obtiene el perfil del usuario desde la API propia.
   * Debe llamarse inmediatamente después de obtener el accessToken de Supabase.
   * Lanza 'account_inactive' si el usuario está desactivado.
   */
  getProfile: async (signal?: AbortSignal): Promise<AuthUser> => {
    const { data } = await httpClient.get<ApiIdentityResponse>(`${API_V1}/identity/me`, { signal });

    if (data.isActive === false) {
      throw new Error('account_inactive');
    }

    const preferences = data.preferences ?? null;

    if (preferences?.language) {
      i18n.changeLanguage(preferences.language);
    }

    if (preferences?.timezone) {
      useUIStore.getState().setTimezone(preferences.timezone);
    }

    if (preferences?.dateFormat) {
      useUIStore
        .getState()
        .setDateFormat(
          preferences.dateFormat as
            | 'dd/MM/yyyy'
            | 'MM/dd/yyyy'
            | 'yyyy-MM-dd'
            | 'dd-MM-yyyy'
            | 'MM-dd-yyyy'
            | 'dd.MM.yyyy'
            | 'd MMM yyyy',
        );
    }

    if (preferences?.timeFormat) {
      useUIStore.getState().setTimeFormat(preferences.timeFormat as TimeFormat);
    }

    return {
      id: data.id,
      email: data.email,
      name: data.displayName ?? data.email,
      avatarUrl: data.avatarUrl ?? undefined,
      color: data.color ?? getUserColor(String(data.id)),
      isActive: data.isActive,
      lastLoginAt: data.lastLoginAt,
      createdAt: data.createdAt,
      preferences,
    };
  },
};
